/**
 * App shell.
 *
 * Nav is grouped by what the merchant is trying to do, not by which tier we
 * shipped the feature in. "Run" is the daily loop, "Intelligence" is what they
 * can't get anywhere else, "Setup" is configured once and forgotten.
 */
import { useEffect, useState } from 'react';
import { NavLink, Outlet } from 'react-router-dom';
import { api, auth, isEmbedded } from '../api';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  badge?: string;
}

const NAV_GROUPS: Array<{ heading: string; items: NavItem[] }> = [
  {
    heading: 'Run',
    items: [
      { to: '/', label: 'Dashboard', end: true },
      { to: '/copilot', label: 'ScaleAds Copilot', badge: 'AI' },
      { to: '/campaigns', label: 'Campaigns' },
      { to: '/creatives', label: 'Creative Studio' },
      { to: '/recommendations', label: 'Recommendations' },
      { to: '/operator', label: 'AI Operator' },
    ],
  },
  {
    heading: 'Intelligence',
    items: [
      { to: '/competitors', label: 'Competitor Intel' },
      { to: '/benchmarks', label: 'Benchmarks & true ROAS' },
      { to: '/reddit', label: 'Reddit Insights' },
      { to: '/funnels', label: 'Funnels' },
    ],
  },
  {
    heading: 'Setup',
    items: [
      { to: '/products', label: 'Products' },
      { to: '/channels', label: 'Ad channels' },
      { to: '/pixel', label: 'Pixel setup' },
      { to: '/billing', label: 'Billing' },
    ],
  },
];

export default function Layout() {
  const [pending, setPending] = useState(0);
  const [connected, setConnected] = useState<string[]>([]);

  useEffect(() => {
    api
      .recommendations('pending')
      .then((r) => setPending(r.length))
      .catch(() => undefined);
    api
      .connections()
      .then((c) => setConnected(c.filter((x) => x.status === 'active').map((x) => x.platform)))
      .catch(() => undefined);
  }, []);

  return (
    <div className="layout">
      <aside className="sidebar">
        <div className="brand">
          ScaleAds AI
        </div>

        <nav>
          {NAV_GROUPS.map((g) => (
            <div key={g.heading} className="nav-group">
              <div className="nav-heading">{g.heading}</div>
              {g.items.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.end}
                  className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
                >
                  <span>{n.label}</span>
                  {n.badge && <em className="nav-badge">{n.badge}</em>}
                  {n.to === '/recommendations' && pending > 0 && (
                    <em className="nav-count">{pending}</em>
                  )}
                </NavLink>
              ))}
            </div>
          ))}
        </nav>

        <div className="store">
          <b>Shopify workspace</b>
          Connected store
          <div className="store-badges">
            {connected.length === 0 ? (
              <NavLink to="/channels" className="badge">
                connect a channel
              </NavLink>
            ) : (
              connected.map((p) => (
                <span key={p} className={`badge b-${p}`}>
                  {p}
                </span>
              ))
            )}
          </div>
        </div>
      </aside>

      <div className="main">
        <div className="topbar">
          <h1>ScaleAds AI</h1>
          <div className="spacer" />
          <NavLink to="/copilot" className="btn">
            Ask Copilot
          </NavLink>
          <NavLink to="/campaigns/new" className="btn btn-primary">
            + Launch campaign
          </NavLink>
          {!isEmbedded() && (
            <button
              className="btn"
              onClick={() => {
                // Best-effort server-side revocation, then clear locally either
                // way — a network failure must not trap someone signed in.
                void api.logout().catch(() => undefined);
                auth.clear();
                window.location.href = '/';
              }}
            >
              Sign out
            </button>
          )}
        </div>
        <div className="content">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
